/**
 * THE profile avatar renderer: the customer's chosen cutout on their chosen
 * background colour. It is a thin framing of `CardFace` — the same
 * art-on-colour primitive `DesignedCardFace` uses — so there is exactly ONE
 * render path for "cutout composited on a colour", not a second one for
 * profiles.
 *
 * Display never reprocesses: `avatarUrl` is the persisted `/cutouts/` URL and
 * is rendered as-is.
 */
import { User } from "lucide-react";
import { CardFace } from "@/components/consumer/chrome";
import { cn } from "@/lib/utils";

const DEFAULT_BG = "#1C2030"; // ink — the palette's neutral, not a new colour

export function ProfileAvatar({
  avatarUrl,
  backgroundColour,
  className,
  alt = "",
  ...rest
}: {
  avatarUrl?: string | null;
  backgroundColour?: string | null;
  className?: string;
  alt?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "color">) {
  if (!avatarUrl) {
    return (
      <div
        className={cn("grid aspect-square w-full shrink-0 place-items-center rounded-full", className)}
        /* aspectRatio is set INLINE as well as by the class: a flex or grid
           parent can shrink a class-only square into an oblong, and that is the
           shape defect that kept coming back on different surfaces. Inline
           style cannot be overridden by a parent's utility classes. */
        style={{ background: backgroundColour || "#FFFFFF", aspectRatio: "1 / 1" }}
        data-testid="profile-avatar-empty"
        {...rest}
      >
        <User className="h-1/2 w-1/2 text-ink/45" strokeWidth={2} />
      </div>
    );
  }
  return (
    <CardFace
      color={backgroundColour || DEFAULT_BG}
      art={avatarUrl}
      alt={alt}
      /* Round, undistorted, and NOT a card: no chip, and the art fills the
         circle from the top so the face is never letterboxed or stretched. */
      chip={false}
      artClassName="object-cover object-top"
      className={cn("aspect-square w-full shrink-0 rounded-full shadow-none pointer-events-none select-none", className)}
      /* Inline 1:1 — see the empty state above. No parent layout can squash it. */
      style={{ aspectRatio: "1 / 1" }}
      data-profile-avatar-bg={backgroundColour || DEFAULT_BG}
      {...rest}
    />
  );
}

export default ProfileAvatar;
